/**
 * SoleadLab 2.0 — Exemplar auto-selection for "Find similar to my marks" (P2a)
 *
 * Server contract (soleadlab-api/src/handlers/detections.ts): a similar scan
 * needs 3..5 DISTINCT exemplar indication ids of the chosen type; the server
 * resolves their geometry from D1 (the sole entity authority), so only marks
 * already synced to the entity table (`_version` stamped) are eligible — an
 * unsynced mark would 400 the create call ("not yet synced to the server").
 *
 * Scoping rule, in priority order (first tier reaching the minimum wins):
 *   (a) 'layer' — marks stamped with properties.orthoLayerId === current
 *       layer id (stamped at creation since P2a, see utils/createIndication.ts);
 *   (b) 'date'  — marks whose captureDate day matches the current layer's
 *       capture day;
 *   (b2) 'name' — layer has NO captureDate, but its NAME carries one
 *       ("5.6.2023.tif", "17.11.2024.jpg" — D.M.YYYY): match marks to it.
 *       Added after the first live scans silently fell through to (c) and
 *       used Nov-2024 marks as exemplars on a Jun-2023 ortho — crops of
 *       HEALTHY pixels (those faults didn't exist yet) = poisoned templates.
 *   (c) 'site'  — all site marks of the type. LAST RESORT; selection carries
 *       a warning when the pool spans multiple capture days so the UI can
 *       surface what is about to be used instead of hiding it.
 * Within the winning tier: the EXEMPLARS_MAX most recent (createdAt primary,
 * day-only captureDate fallback — see utils/captureDate.compareCaptureOrder).
 */

import type { Indication, IndicationType, Orthophoto } from '../stores/projectStore';
import { INDICATION_TYPES } from '../stores/projectStore';
import { captureDay, compareCaptureOrder } from './captureDate';

/** Mirrors EXEMPLARS_MIN/MAX in soleadlab-api/src/handlers/detections.ts. */
export const EXEMPLARS_MIN = 3;
export const EXEMPLARS_MAX = 5;

function centroidOf(i: Indication): [number, number] {
  const geom = i.geometry as { coordinates?: number[][][] } | undefined;
  const ring = geom?.coordinates?.[0];
  if (!ring?.length) return [0, 0];
  const pts = ring.length > 1 ? ring.slice(0, -1) : ring;
  let sx = 0;
  let sy = 0;
  for (const p of pts) {
    sx += p[0];
    sy += p[1];
  }
  return [sx / pts.length, sy / pts.length];
}

function distanceMeters(a: [number, number], b: [number, number]): number {
  const mLng = 111320 * Math.cos((((a[1] + b[1]) / 2) * Math.PI) / 180);
  return Math.hypot((a[0] - b[0]) * mLng, (a[1] - b[1]) * 110540);
}

export type ExemplarTier = 'layer' | 'date' | 'name' | 'site';

export interface ExemplarSelection {
  /** Up to EXEMPLARS_MAX ids, most recent first. */
  ids: string[];
  tier: ExemplarTier;
  /** true when ids.length >= EXEMPLARS_MIN (the API minimum). */
  eligible: boolean;
  /** Capture day (YYYY-MM-DD) shared by the selected exemplars, when they
   *  agree on one — surfaced in the Scan menu so the user SEES the epoch. */
  captureDay?: string;
  /** Set when the selection is risky (site-wide fallback across mixed
   *  capture days — exemplar crops may come from a different epoch than the
   *  scanned ortho). The UI must show this. */
  warning?: string;
}

/** "5.6.2023.tif" / "17.11.2024.jpg" / "26.5.2024.tif" -> "2023-06-05" etc.
 *  D.M.YYYY or DD.MM.YYYY anywhere in the layer name; null when absent. */
export function parseLayerNameDay(name: string | undefined): string | null {
  if (!name) return null;
  const m = /(\d{1,2})\.(\d{1,2})\.(\d{4})/.exec(name);
  if (!m) return null;
  const [, d, mo, y] = m;
  const day = Number(d);
  const month = Number(mo);
  if (day < 1 || day > 31 || month < 1 || month > 12) return null;
  return `${y}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function mostRecentIds(pool: Indication[]): string[] {
  return [...pool]
    .sort((a, b) => compareCaptureOrder(b.properties, a.properties)) // newest first
    .slice(0, EXEMPLARS_MAX)
    .map((i) => i.id);
}

/**
 * Resolve the exemplar set for `type` against the current ortho layer.
 * Always returns the best-available selection; check `.eligible` before
 * dispatching a scan (the API rejects < EXEMPLARS_MIN).
 */
export function resolveExemplars(
  indications: Indication[],
  type: IndicationType,
  ortho: Orthophoto,
): ExemplarSelection {
  // Server-synced marks only (see header) — geometry is resolved from D1.
  // UN-MOVED photo-children are EXCLUDED: a photo-classification child is
  // born AT the photo's GPS (drone over the row gap). The owner's workflow
  // then MOVES it onto the fault module (and those are perfectly good
  // exemplars) — but a child still sitting within ~2 m of its source photo
  // was never repositioned and teaches the scanner ground texture. (A live
  // Gadot scan that learned from three un-moved stragglers proved this.)
  const photoPos = new Map<string, [number, number]>();
  for (const p of indications) {
    if (p.type === 'Photo') photoPos.set(p.id, centroidOf(p));
  }
  const synced = indications.filter((i) => {
    if (i.type !== type || i._version == null) return false;
    const src = (i.properties as { sourcePhotoId?: string }).sourcePhotoId;
    if (src == null) return true;
    const ph = photoPos.get(src);
    if (!ph) return true; // photo not loaded — can't judge, keep
    return distanceMeters(centroidOf(i), ph) > 2.0; // moved => keep
  });

  const onLayer = synced.filter((i) => i.properties.orthoLayerId === ortho.id);
  if (onLayer.length >= EXEMPLARS_MIN) {
    const days = new Set(onLayer.map((i) => captureDay(i.properties.captureDate)));
    return {
      ids: mostRecentIds(onLayer),
      tier: 'layer',
      eligible: true,
      captureDay: days.size === 1 ? [...days][0] ?? undefined : undefined,
    };
  }

  const day = captureDay(ortho.captureDate);
  const onDate = day
    ? synced.filter((i) => captureDay(i.properties.captureDate) === day)
    : [];
  if (onDate.length >= EXEMPLARS_MIN) {
    return { ids: mostRecentIds(onDate), tier: 'date', eligible: true, captureDay: day ?? undefined };
  }

  // (b2) layer name carries a date the layer metadata lacks
  const nameDay = day ? null : parseLayerNameDay(ortho.name);
  const onNameDate = nameDay
    ? synced.filter((i) => captureDay(i.properties.captureDate) === nameDay)
    : [];
  if (onNameDate.length >= EXEMPLARS_MIN) {
    return { ids: mostRecentIds(onNameDate), tier: 'name', eligible: true, captureDay: nameDay ?? undefined };
  }

  // (c) last resort — NEVER silent: warn when the site pool spans epochs,
  // because most-recent marks may not exist on the scanned ortho at all.
  const siteDays = new Set(
    synced.map((i) => captureDay(i.properties.captureDate)).filter(Boolean),
  );
  return {
    ids: mostRecentIds(synced),
    tier: 'site',
    eligible: synced.length >= EXEMPLARS_MIN,
    captureDay: siteDays.size === 1 ? [...siteDays][0] ?? undefined : undefined,
    warning:
      siteDays.size > 1
        ? 'Using the most recent marks on the site — they span multiple capture dates and may not match this ortho. Set the layer capture date to scope them.'
        : undefined,
  };
}

export interface SimilarEligibleType {
  type: IndicationType;
  selection: ExemplarSelection;
}

/**
 * Counted (fault) types whose exemplar resolution reaches the API minimum on
 * the current ortho — the entries of the Scan menu's "Find similar" list.
 * Non-counted documentation types (Photo/Video/DataPoint/…) are never offered.
 */
export function listSimilarEligibleTypes(
  indications: Indication[],
  ortho: Orthophoto,
): SimilarEligibleType[] {
  const out: SimilarEligibleType[] = [];
  for (const type of Object.keys(INDICATION_TYPES) as IndicationType[]) {
    if (!INDICATION_TYPES[type].counted) continue;
    const selection = resolveExemplars(indications, type, ortho);
    if (selection.eligible) out.push({ type, selection });
  }
  return out;
}
